import { type SpendingControlControlType } from "@clipboard-health/contract-backend-main";

/**
 * Spending-control category. `total` caps overall workplace spend; `variableCharge`
 * caps boost (variable-charge) spend, which is a subset of `total`. Sourced from the
 * `@clipboard-health/contract-backend-main` v2 spending-controls contract.
 */
export type ControlType = SpendingControlControlType;

/** Frontend-ergonomic const map mirroring the contract's spending-control types. */
export const CONTROL_TYPES = {
  total: "total",
  variableCharge: "variableCharge",
} as const satisfies Record<string, ControlType>;

export function isBoostControlType(controlType: ControlType): boolean {
  return controlType === CONTROL_TYPES.variableCharge;
}

export interface SelectedWorkplace {
  id: string;
  name: string;
  /** Which control the open dialog edits, so create/delete are scoped to it. */
  controlType: ControlType;
  currentLimitInDollars?: number;
  currentLimitInHours?: number;
  currentEndDate?: Date;
}

/**
 * A v2 spending-control row joined with its usage (from the `included`
 * `spending-control-usage` resources). Usage amounts default to 0 when the row
 * has no upstream usage data.
 *
 * The verified/planned fields are flattened to the top level so this object is
 * directly consumable by {@link mapToSpendSegments} and
 * {@link calculateSummaryMetrics}.
 */
export interface SpendingControlWithUsage {
  id: string;
  workplaceId: string;
  controlType: ControlType;
  amountInCents?: number;
  amountInHours?: number;
  startDate?: Date;
  endDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  verifiedAmountInCents: number;
  plannedAmountInCents: number;
  verifiedHours: number;
  plannedHours: number;
}
